import React, { useState, useMemo } from 'react';
import { Box, TextField, Typography, List, ListItem, CircularProgress, Checkbox, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

function DTTagTree({ tree, loading, selectedTagIds, onTagToggle, maxDatapoints, theme }) {
  const [search, setSearch] = useState('');
  const isDark = theme === 'dark';

  const filtered = useMemo(() => {
    if (!search.trim()) return tree;
    const q = search.toLowerCase();
    return tree.filter(
      (item) =>
        (item.title || '').toLowerCase().includes(q) ||
        String(item.tagId || '').includes(q) ||
        (item.deviceName || '').toLowerCase().includes(q)
    );
  }, [tree, search]);

  const canAdd = (tagId) => {
    if (selectedTagIds.includes(String(tagId))) return true;
    return selectedTagIds.length < (maxDatapoints || 1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        placeholder="Search tags..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: isDark ? '#888' : '#666' }} /></InputAdornment>,
        }}
        sx={{ mb: 1, '& .MuiOutlinedInput-root': { color: isDark ? '#fff' : undefined } }}
      />
      {filtered.length === 0 ? (
        <Typography variant="body2" sx={{ color: isDark ? '#666' : '#aaa', textAlign: 'center', py: 2 }}>
          {tree.length === 0 ? 'No tags available. Check project configuration.' : 'No tags match your search.'}
        </Typography>
      ) : (
        <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto', border: `1px solid ${isDark ? '#333' : '#eee'}`, borderRadius: 1 }}>
          {filtered.map((item) => {
            const idStr = String(item.tagId);
            const isSelected = selectedTagIds.includes(idStr);
            const isDisabled = !isSelected && !canAdd(item.tagId);
            return (
              <ListItem
                key={item.key || item.tagId}
                disablePadding
                onClick={() => !isDisabled && onTagToggle(item)}
                sx={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.4 : 1,
                  padding: '2px 8px',
                  '&:hover': !isDisabled ? { backgroundColor: isDark ? '#1e3a5f' : '#e3f2fd' } : {},
                }}
              >
                <Checkbox size="small" checked={isSelected} disabled={isDisabled} sx={{ padding: 0.5 }} />
                <Box sx={{ ml: 0.5 }}>
                  <Typography variant="caption" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500, display: 'block', lineHeight: 1.2 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#888' : '#999', fontSize: 10 }}>
                    ID: {item.tagId}{item.unit ? ` · ${item.unit}` : ''}{item.deviceName ? ` · ${item.deviceName}` : ''}
                  </Typography>
                </Box>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
}

export default React.memo(DTTagTree);
